import { type FC, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ZocksDataTable } from '../../components/DataTable/ZocksDataTable'
import { useZocksAuth, useZocksQuota } from '../../components/ScopedBlock/hooks'
import { type InviteResponse } from '../../store/auth/auth'
import { selectAuthenticatedUser } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { type PageMetadata } from '../../models/PageMetadata'
import { type User } from '../../models/User'
import { DeactivateUserModal } from './DeactivateUserModal'
import { DeleteInviteModal } from './DeleteInviteModal'
import { DeleteUserModal } from './DeleteUserModal'
import { EditUserModal } from './EditUserModal'
import { InviteUserActions } from './InviteUserActions'
import { RequestedSeatGroups } from './RequestedSeatGroups'
import { ResendInviteModal } from './ResendInviteModal'
import { TeamChipBar } from './TeamChipBar'
import { TeamEmptyState } from './TeamEmptyState'
import { TeamListFilters } from './TeamListFilters'
import { buildTeamActionsColumn, buildTeamColumns } from './teamColumns'
import { type TeamRow } from './teamRows'
import { useRefreshSeats } from './useRefreshSeats'
import { useTeamList } from './useTeamList'
import { useTeamListUrlState } from './useTeamListUrlState'

const FAILED_TO_DELIVER_ROW_STYLES = [
  {
    when: (row: TeamRow) => row.status === 'failedToDeliver',
    style: { backgroundColor: 'var(--zocks-warning-background)' },
  },
]

type ActiveModal =
  | { kind: 'editUser'; userId: string }
  | { kind: 'deleteUser'; user: User }
  | { kind: 'deactivateUser'; user: User }
  | { kind: 'resendInvite'; invite: InviteResponse }
  | { kind: 'deleteInvite'; invite: InviteResponse }
  | null

export interface UnifiedTeamListProps {
  usersPage: PageMetadata | undefined
}

export const UnifiedTeamList: FC<UnifiedTeamListProps> = ({ usersPage }) => {
  const { userId = null, modal = null } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)
  const { hasScope } = useZocksAuth()
  const { hasQuota } = useZocksQuota()
  const hasInviteScope = hasScope('**', 'consoleuser:invite', ['consoleuser:admin'])
  const userDeactivationEnabled = hasQuota('auth.userDeactivationEnabled.value')

  const [activeModal, setActiveModal] = useState<ActiveModal>(modal === 'edit' && userId ? { kind: 'editUser', userId } : null)
  const refreshSeats = useRefreshSeats()

  const urlState = useTeamListUrlState(hasInviteScope)
  const { chip, page, requestedPages, perPage, namePattern, roleGroupNames, roleGroupOptions, hasActiveFilters } = urlState
  const showsGroups = chip === 'requested'
  const { rows, totalRows, isFetching, counts, refetch } = useTeamList({
    chip,
    page,
    perPage,
    hasInviteScope,
    namePattern,
    roleGroupNames,
  })

  const openEditUser = (user: User) => {
    setActiveModal({ kind: 'editUser', userId: user.id })
    navigate({ pathname: `/account/manage-team/users/${user.id}/edit`, search: searchParams.toString() }, { replace: true })
  }

  const closeEditUser = () => {
    setActiveModal(null)
    navigate({ pathname: '/account/manage-team/', search: searchParams.toString() }, { replace: true })
  }

  const closeModal = () => setActiveModal(null)

  const closeModalAndReload = () => {
    setActiveModal(null)
    refetch()
    refreshSeats()
  }

  const columns = [
    ...buildTeamColumns(authenticatedUser, { showExpiresAt: chip === 'pending' || chip === 'expired' }),
    buildTeamActionsColumn({
      userDeactivationEnabled,
      onEditUser: openEditUser,
      onDeactivateUser: (user) => setActiveModal({ kind: 'deactivateUser', user }),
      onDeleteUser: (user) => setActiveModal({ kind: 'deleteUser', user }),
      onResendInvite: (invite) => setActiveModal({ kind: 'resendInvite', invite }),
      onDeleteInvite: (invite) => setActiveModal({ kind: 'deleteInvite', invite }),
    }),
  ]

  return (
    <Container className='p-0'>
      {activeModal?.kind === 'editUser' ? <EditUserModal userId={activeModal.userId} onHide={closeEditUser} onUserEdited={refetch} /> : null}
      {activeModal?.kind === 'deleteUser' ? <DeleteUserModal user={activeModal.user} show onHide={closeModal} onUserDeleted={closeModalAndReload} /> : null}
      {activeModal?.kind === 'deactivateUser' ? (
        <DeactivateUserModal user={activeModal.user} onHide={closeModal} onUserDeactivated={closeModalAndReload} />
      ) : null}
      {activeModal?.kind === 'resendInvite' ? <ResendInviteModal invite={activeModal.invite} show onHide={closeModal} /> : null}
      {activeModal?.kind === 'deleteInvite' ? <DeleteInviteModal invite={activeModal.invite} show onHide={closeModal} /> : null}
      <Row className='mb-6 gy-3 align-items-center'>
        <Col className='p-0'>
          <InviteUserActions usersPage={usersPage} />
        </Col>
      </Row>
      <Row className='mb-4 gy-3 align-items-center'>
        <Col xs={12} lg='auto' className='p-0'>
          <TeamChipBar activeChip={chip} counts={counts} hasInviteScope={hasInviteScope} onSelect={urlState.setChip} />
        </Col>
        <Col className='p-0'>
          <TeamListFilters
            namePattern={namePattern}
            roleGroupNames={roleGroupNames}
            roleGroupOptions={roleGroupOptions}
            hasActiveFilters={hasActiveFilters}
            onNamePatternChange={urlState.setNamePattern}
            onRoleGroupNamesChange={urlState.setRoleGroupNames}
            onClearFilters={urlState.clearFilters}
          />
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          {showsGroups ? (
            <RequestedSeatGroups
              pages={requestedPages}
              perPage={perPage}
              namePattern={namePattern}
              roleGroupNames={roleGroupNames}
              buySeatFor={urlState.buySeatFor}
              onBuySeatForHandled={urlState.clearBuySeatFor}
              onChangePage={urlState.setRequestedPage}
              onChangePerPage={urlState.setPerPage}
            />
          ) : (
            <ZocksDataTable
              columns={columns}
              data={rows}
              conditionalRowStyles={FAILED_TO_DELIVER_ROW_STYLES}
              progressPending={isFetching}
              noDataComponent={
                hasActiveFilters ? (
                  <div className='paragraph-small zocks-text-gray-600 mb-0 py-3'>No results match your filters.</div>
                ) : (
                  <TeamEmptyState chip={chip} />
                )
              }
              pagination
              paginationServer
              paginationDefaultPage={page}
              paginationPerPage={perPage}
              paginationTotalRows={totalRows}
              onChangePage={urlState.setPage}
              onChangeRowsPerPage={urlState.setPerPage}
            />
          )}
        </Col>
      </Row>
    </Container>
  )
}
